const DATASET_LABELS = {
  clinical_trials: "Clinical Trials",
  pubmed: "PubMed",
  fda_510k: "FDA 510(k)",
  fda_denovo: "FDA De Novo",
  de_novo: "FDA De Novo",
  fda_pma: "FDA PMA",
  fda_drugs: "FDA Drugs",
  google_patents: "Google Patents",
  patents: "Google Patents",
};

const PIPELINE_STAGE_ORDER = [
  "Preclinical",
  "Phase 1",
  "Phase 2",
  "Phase 2/3",
  "Phase 3",
  "Approved",
  "Discontinued",
];

const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

const COMPANY_STAT_CONFIG = [
  { key: "related_records", label: "Related Records", datasets: null },
  { key: "google_patents", label: "Patents", datasets: ["google_patents"] },
  { key: "clinical_trials", label: "Trials", datasets: ["clinical_trials"] },
  { key: "fda_records", label: "FDA Records", datasets: ["fda_510k", "fda_denovo", "fda_pma"] },
  { key: "pubmed", label: "Publications", datasets: ["pubmed"] },
  { key: "fda_drugs", label: "Drug Products", datasets: ["fda_drugs"] },
];

const LINKED_ENTITY_COLLECTION_LABELS = {
  patents: "Patents",
  clinical_trials: "Clinical Trials",
  pubmed: "Publications",
  fda_510k: "FDA Clearances",
  de_novo: "FDA Clearances",
  fda_denovo: "FDA Clearances",
  fda_pma: "FDA Clearances",
  fda_drugs: "Products",
};

const LINKED_ENTITY_DATASET_KEYS = {
  patents: "google_patents",
  clinical_trials: "clinical_trials",
  pubmed: "pubmed",
  fda_510k: "fda_510k",
  de_novo: "fda_denovo",
  fda_denovo: "fda_denovo",
  fda_pma: "fda_pma",
  fda_drugs: "fda_drugs",
};

const uniqueValues = (values) => [...new Set((values || []).filter(Boolean))];

const formatHeaderValue = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "N/A";
  }

  return normalized
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const normalizeHeaderText = (value) => {
  const normalized = String(value || "").trim();
  return normalized || "N/A";
};

const createDatasetAccumulator = (datasetKey) => ({
  datasetKey,
  label: DATASET_LABELS[datasetKey] || datasetKey,
  count: 0,
  mentions: 0,
  roles: [],
  items: [],
});

const normalizeRoleLabel = (roles = []) => {
  const normalizedRoles = uniqueValues(roles.map((role) => String(role || "").trim()));
  return normalizedRoles.length ? normalizedRoles.join(", ") : "";
};

const normalizeSourceBuckets = (sources) => {
  if (!Array.isArray(sources) || !sources.length) {
    return [];
  }

  const first = sources[0];
  if (first?.collection_name) {
    return sources;
  }

  return sources.flatMap((sourceDoc) => {
    const collections = Array.isArray(sourceDoc?.collections) ? sourceDoc.collections : [];
    return collections.map((entry) => ({
      collection_name: entry?.collection || entry?.source || "",
      dataset: entry?.dataset || entry?.source || "",
      field: normalizeRoleLabel(Array.isArray(entry?.roles) ? entry.roles : []),
      source_doc_ids: uniqueValues(entry?.source_doc_ids || []),
      docs_count: Number(entry?.match_count || 0),
    }));
  });
};

const getCompanySummary = (record) =>
  record?.company_summary && typeof record.company_summary === "object"
    ? record.company_summary
    : null;

const normalizeCompanyAliasValues = (values) =>
  uniqueValues(
    (Array.isArray(values) ? values : []).map((value) => String(value || "").trim())
  );

const normalizeCompanyPersonValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const normalized = normalizeCompanyPersonValue(item);
      if (normalized) {
        return normalized;
      }
    }
    return null;
  }

  if (typeof value === "object") {
    return normalizeCompanyPersonValue(
      value.name || value.full_name || value.label || value.text || value.value || null
    );
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  const objectLikeName = normalized.match(
    /^\{\s*['"]?name['"]?\s*:\s*['"](.+?)['"]\s*\}$/
  );
  return objectLikeName ? objectLikeName[1] : normalized;
};

const buildSummaryDatasetGroups = (summary) => {
  const datasets = summary?.datasets;
  if (!datasets || typeof datasets !== "object") {
    return [];
  }

  const mapping = [
    ["clinical_trials", "clinical_trials"],
    ["pubmed", "pubmed"],
    ["fda_510k", "fda_510k"],
    ["de_novo", "de_novo"],
    ["fda_pma", "fda_pma"],
    ["fda_drugs", "fda_drugs"],
    ["google_patents", "google_patents"],
  ];

  return mapping
    .map(([summaryKey, datasetKey]) => {
      const group = datasets?.[summaryKey];
      const count = Number(group?.total_count || 0);
      if (!count) {
        return null;
      }
      return {
        datasetKey,
        label: DATASET_LABELS[datasetKey] || datasetKey,
        count,
        mentions: count,
        roles: [],
        items: (group?.public_ids || []).map((publicId) => ({
          recordId: String(publicId || "").trim(),
          label: String(publicId || "").trim(),
          role: "",
          source: DATASET_LABELS[datasetKey] || datasetKey,
        })),
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }
      return left.label.localeCompare(right.label);
    });
};

const buildLegacyDatasetGroups = (sources) => {
  const sourceBuckets = normalizeSourceBuckets(sources);
  if (!sourceBuckets.length) {
    return [];
  }

  const datasetMap = new Map();
  sourceBuckets.forEach((entry) => {
    const datasetKey = entry?.dataset;
    if (!datasetKey) {
      return;
    }

    const current = datasetMap.get(datasetKey) || createDatasetAccumulator(datasetKey);
    const sourceIds = uniqueValues(
      (Array.isArray(entry?.source_doc_ids) ? entry.source_doc_ids : []).map((item) =>
        String(item || "").trim()
      )
    );
    const roles = uniqueValues([String(entry?.field || "").trim()].filter(Boolean));

    current.mentions += Number(entry?.docs_count || 0);
    current.roles = uniqueValues([...current.roles, ...roles]);

    sourceIds.forEach((recordId) => {
      if (current.items.some((item) => item.recordId === recordId)) {
        return;
      }
      current.items.push({
        recordId,
        label: recordId,
        role: normalizeRoleLabel(roles),
        source: entry?.collection_name || "",
      });
    });

    current.count = current.items.length;
    datasetMap.set(datasetKey, current);
  });

  return Array.from(datasetMap.values())
    .filter((group) => group.count > 0)
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }
      return left.label.localeCompare(right.label);
    });
};

export const getCompanyDatasetLabel = (datasetKey) =>
  DATASET_LABELS[datasetKey] || datasetKey;

export const getCompanyAliasPreview = (aliases) => {
  if (!Array.isArray(aliases) || !aliases.length) {
    return "No aliases";
  }
  if (aliases.length === 1) {
    return aliases[0];
  }
  return `${aliases[0]} +${aliases.length - 1}`;
};

export const buildCompanyAliases = (record) => {
  const summary = getCompanySummary(record);
  return normalizeCompanyAliasValues([
    ...(Array.isArray(summary?.aliases) ? summary.aliases : []),
    ...(Array.isArray(record?.aliases) ? record.aliases : []),
  ]);
};

export const buildTopProducts = (record) => {
  const summary = getCompanySummary(record);
  return Array.isArray(summary?.top_products) ? summary.top_products : [];
};

export const buildCompanyDatasetGroups = (recordOrSources) => {
  const sourceInput =
    Array.isArray(recordOrSources) || recordOrSources == null
      ? recordOrSources
      : recordOrSources?.sources;
  const summary = Array.isArray(recordOrSources) ? null : getCompanySummary(recordOrSources);

  if (summary) {
    const groups = buildSummaryDatasetGroups(summary);
    if (groups.length) {
      return groups;
    }
  }
  return buildLegacyDatasetGroups(sourceInput);
};

export const getCompanyCoverageSummary = (recordOrSources) => {
  const datasetGroups = buildCompanyDatasetGroups(recordOrSources);
  if (!datasetGroups.length) {
    return "N/A";
  }
  return datasetGroups.map((group) => group.label).join(", ");
};

export const buildCompanyCoverageGroups = (recordOrSources) =>
  buildCompanyDatasetGroups(recordOrSources).map((group) => ({
    datasetKey: group.datasetKey,
    label: group.label,
    docs: group.count,
    mentions: group.mentions,
    roles: group.roles,
  }));

export const buildCompanyStatItems = (record) => {
  const summary = getCompanySummary(record);
  if (summary) {
    const values = {
      related_records:
        Number(summary.total_trials || 0) +
        Number(summary.total_patents || 0) +
        Number(summary.total_pubmed || 0) +
        Number(summary.total_fda_products?.total || 0),
      google_patents: Number(summary.total_patents || 0),
      clinical_trials: Number(summary.total_trials || 0),
      fda_records:
        Number(summary.total_fda_products?.fda_510k || 0) +
        Number(summary.total_fda_products?.de_novo || 0) +
        Number(summary.total_fda_products?.pma || 0),
      pubmed: Number(summary.total_pubmed || 0),
      fda_drugs: Number(summary.total_fda_products?.drugs || 0),
    };
    return COMPANY_STAT_CONFIG.map((item) => ({
      key: item.key,
      label: item.label,
      value: Number(values[item.key] || 0),
    }));
  }

  const datasetGroups = buildCompanyDatasetGroups(record);
  const countByDataset = datasetGroups.reduce((accumulator, group) => {
    accumulator[group.datasetKey] = group.count;
    return accumulator;
  }, {});
  const relatedRecordCount = datasetGroups.reduce(
    (total, group) => total + Number(group.count || 0),
    0
  );

  return COMPANY_STAT_CONFIG.map((item) => ({
    key: item.key,
    label: item.label,
    value:
      item.datasets === null
        ? relatedRecordCount
        : item.datasets.reduce(
            (total, datasetKey) => total + Number(countByDataset[datasetKey] || 0),
            0
          ),
  }));
};

export const buildCompanySourceNameList = (record) =>
  uniqueValues([
    String(record?.company_summary?.company_name || "").trim(),
    String(record?.ai_name || "").trim(),
    String(record?.raw_name || "").trim(),
    ...(Array.isArray(record?.company_summary?.aliases)
      ? record.company_summary.aliases.map((item) => String(item || "").trim())
      : []),
    ...(Array.isArray(record?.aliases)
      ? record.aliases.map((item) => String(item || "").trim())
      : []),
  ]);

export const buildCompanyOverview = (record) => {
  const summary = getCompanySummary(record);
  const coverageGroups = buildCompanyCoverageGroups(record);
  const activeSinceRaw = summary?.years_active_start_date || "";
  const activeSinceYear = /^\d{4}/.test(activeSinceRaw) ? activeSinceRaw.slice(0, 4) : null;
  const assigneeType = summary?.assignee_type || record?.assignee_type || "";
  const country = summary?.country || record?.country || "";

  return {
    companyName:
      String(summary?.company_name || "").trim() || String(record?.ai_name || "").trim() || "Unknown company",
    aliases: buildCompanyAliases(record),
    assigneeType: formatHeaderValue(assigneeType),
    country: normalizeHeaderText(country),
    activeSinceYear,
    datasetCoverage: coverageGroups.map((group) => group.label),
  };
};

export const buildCompanyKpis = (record) => {
  const summary = getCompanySummary(record);
  if (!summary) {
    const stats = buildCompanyStatItems(record);
    return {
      patents: Number(stats.find((item) => item.key === "google_patents")?.value || 0),
      trials: Number(stats.find((item) => item.key === "clinical_trials")?.value || 0),
      fdaProducts: Number(stats.find((item) => item.key === "fda_records")?.value || 0),
      publications: Number(stats.find((item) => item.key === "pubmed")?.value || 0),
      activeProducts: Number(stats.find((item) => item.key === "fda_drugs")?.value || 0),
    };
  }
  return {
    patents: Number(summary.total_patents || 0),
    trials: Number(summary.total_trials || 0),
    fdaProducts: Number(summary.total_fda_products?.total || 0),
    publications: Number(summary.total_pubmed || 0),
    activeProducts: Number(summary.total_fda_products?.drugs || 0),
  };
};

export const buildCompanyPipeline = (record) => {
  const summary = getCompanySummary(record);
  const source = summary?.pipeline_by_stage || {};
  return PIPELINE_STAGE_ORDER.map((stage) => ({
    stage,
    count: Number(source?.[stage] || 0),
  }));
};

export const buildCompanyPipelineCells = (record) =>
  buildCompanyPipeline(record).map((item) => ({
    ...item,
    isActive: item.count > 0,
  }));

export const buildCompanyTopProducts = (record) => {
  const summary = getCompanySummary(record);
  if (Array.isArray(summary?.top_products)) {
    return summary.top_products.filter(Boolean).slice(0, 5);
  }
  return [];
};

export const buildCompanyHeaderTags = (record) =>
  buildCompanyTopProducts(record).slice(0, 3).map((label) => ({
    id: String(label || "").trim().toLowerCase(),
    label: String(label || "").trim(),
  }));

export const buildCompanyKeyPeople = (record) => {
  const summary = getCompanySummary(record);
  return {
    topInventor: normalizeCompanyPersonValue(summary?.top_inventor),
    topInvestigator: normalizeCompanyPersonValue(summary?.top_investigator),
  };
};

export const buildCompanySignals = (record) => {
  const summary = getCompanySummary(record);
  return {
    platformStrategySignal:
      summary?.platform_strategy_signal &&
      typeof summary.platform_strategy_signal === "object"
        ? summary.platform_strategy_signal
        : null,
    executionQualitySignal: summary?.execution_quality_signal || null,
    pipelineGapSignal: summary?.pipeline_gap_signal || null,
  };
};

const formatPlatformSignalMessage = (platformSignal) => {
  if (!platformSignal || typeof platformSignal !== "object") {
    return null;
  }

  const explicitMessage = String(platformSignal.message || "").trim();
  if (explicitMessage) {
    return explicitMessage;
  }

  const qualifyingClusterCount = Number(platformSignal.qualifying_cluster_count || 0);
  const clusterPatentCounts = platformSignal.cluster_patent_counts || {};
  const totalPatentsInClusters = Object.values(clusterPatentCounts).reduce(
    (total, value) => total + Number(value || 0),
    0
  );
  const breadthScoreRaw = Number(platformSignal.breadth_score);
  const breadthScore = Number.isFinite(breadthScoreRaw) ? breadthScoreRaw.toFixed(1) : null;
  const normalizedSignal = String(platformSignal.platform_signal || "").toLowerCase();
  const isPlatform = normalizedSignal === "platform";

  if (!qualifyingClusterCount && !totalPatentsInClusters && !breadthScore) {
    return null;
  }

  if (isPlatform) {
    return `Platform strategy: ${qualifyingClusterCount} distinct CPC technology clusters across ${totalPatentsInClusters} patents${breadthScore ? ` - breadth score ${breadthScore}/10` : ""}`;
  }

  return `Not Platform: threshold not met (${qualifyingClusterCount}/3 qualifying CPC clusters with >=2 patents each)${breadthScore ? ` - breadth score ${breadthScore}/10` : ""}`;
};

export const buildCompanyRecentActivity = (record) => {
  const summary = getCompanySummary(record);
  if (!Array.isArray(summary?.recent_activity)) {
    return [];
  }
  return summary.recent_activity.slice(0, 6).map((item) => ({
    datasetLabel: getCompanyDatasetLabel(item?.dataset),
    publicId: String(item?.public_id || "").trim(),
    title: String(item?.title || "").trim(),
    date: String(item?.date || "").trim(),
    url: String(item?.url || "").trim(),
  }));
};

const toActivityDateLabel = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value || "");
  }
  return MONTH_YEAR_FORMATTER.format(parsed);
};

export const buildCompanyRecentActivityRows = (record) =>
  buildCompanyRecentActivity(record).map((item, index) => ({
    id: `${item.datasetLabel}-${item.publicId}-${item.date}-${index}`,
    dateLabel: toActivityDateLabel(item.date),
    label: item.title || item.publicId,
    detail: item.publicId,
    url: item.url,
  }));

const getLinkedEntityCollectionLabel = (collectionKey) =>
  LINKED_ENTITY_COLLECTION_LABELS[collectionKey] || collectionKey;

export const getLinkedEntityDatasetKey = (collectionKey) =>
  LINKED_ENTITY_DATASET_KEYS[collectionKey] || collectionKey;

export const buildCompanyLinkedEntities = (record) => {
  const summary = getCompanySummary(record);
  if (!summary?.public_links || typeof summary.public_links !== "object") {
    return [];
  }

  return Object.entries(summary.public_links).flatMap(([collection, items]) =>
    Array.isArray(items)
      ? items
          .map((item) => {
            const id = String(item?.public_id || "").trim();
            if (!id) {
              return null;
            }

            return {
              id,
              title: String(item?.title || "").trim() || id,
              collection,
              collectionLabel: getLinkedEntityCollectionLabel(collection),
              datasetKey: getLinkedEntityDatasetKey(collection),
              datasetLabel: getCompanyDatasetLabel(getLinkedEntityDatasetKey(collection)),
              publicUrl: String(item?.url || "").trim(),
            };
          })
          .filter(Boolean)
      : []
  );
};

export const buildCompanyLinkedEntityGroups = (record) => {
  const linkedEntities = buildCompanyLinkedEntities(record);
  const groups = new Map();

  linkedEntities.forEach((entity) => {
    const current = groups.get(entity.datasetKey) || {
      datasetKey: entity.datasetKey,
      datasetLabel: entity.datasetLabel,
      items: [],
    };

    current.items.push(entity);
    groups.set(entity.datasetKey, current);
  });

  return Array.from(groups.values());
};

export const buildCompanySignalRows = (record) => {
  const signals = buildCompanySignals(record);
  const rows = [];

  if (signals.platformStrategySignal) {
    const signalText = formatPlatformSignalMessage(signals.platformStrategySignal);
    const isPlatform =
      String(signals.platformStrategySignal.platform_signal || "").toLowerCase() ===
      "platform";
    if (signalText) {
      rows.push({
        id: "platform-strategy",
        tone: isPlatform ? "info" : "neutral",
        text: signalText,
      });
    }
  }

  if (signals.executionQualitySignal) {
    rows.push({
      id: "execution-quality",
      tone: "info",
      text: signals.executionQualitySignal,
    });
  }

  if (signals.pipelineGapSignal) {
    rows.push({
      id: "pipeline-gap",
      tone: "warning",
      text: signals.pipelineGapSignal,
    });
  }

  return rows;
};
